import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { musicServicesByMusicAPIID } from 'fym-shared/src/music-services/allMusicServices';
import { MusicAPIIntegrationID } from 'fym-shared/src/music-services/ids';
import { TypedMemo } from '../../../common/types';
import { SMLSwitch } from '../controls/SMLSwitch';
import { catalogLinksMessages as m } from './catalogLinksMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for several parts; each part uses its own slice of it */
import styles from './CatalogLinks.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface CatalogLinksServiceRowProps {
  /** A `MusicAPIIntegrationID` value. */
  service: string;
  /** The grey line: an address, or "Shows on release day". */
  sub: string | null;
  /** The chip at the end: "Pre-save", "Found", "Not found yet". */
  chip: string | null;
  chipTone?: 'ok' | 'wait' | 'plain';
  /** Undefined: the row has no switch (a play-only service, a found link). */
  isOn?: boolean;
  disabled?: boolean;
  onToggle?: (service: string, on: boolean) => void;
}

/** One service of the Catalog links block (design c1, c2): the mark, the name, a chip and a switch. */
const CatalogLinksServiceRowComponent: React.FC<CatalogLinksServiceRowProps> = ({
  service,
  sub,
  chip,
  chipTone = 'plain',
  isOn,
  disabled,
  onToggle,
}) => {
  const intl = useIntl();
  const info = musicServicesByMusicAPIID[service];
  const name = info?.name ?? service;
  // The mark takes the brand colour of the service: data, not a theme colour.
  const markStyle = useMemo(() => ({ background: info?.color.primary }), [info]);
  const toggle = useCallback((on: boolean) => onToggle?.(service, on), [onToggle, service]);

  return (
    <div className={clsx(styles.service, isOn === false && styles.serviceOff)}>
      <span className={styles.mark} style={markStyle}>
        {name.slice(0, 1)}
      </span>
      <span className={styles.serviceText}>
        {name}
        {sub !== null && <small>{sub}</small>}
      </span>
      {chip !== null && (
        <span className={clsx(styles.chip, chipTone === 'ok' && styles.chipOk, chipTone === 'wait' && styles.chipWait)}>
          {chip}
        </span>
      )}
      {isOn !== undefined && onToggle !== undefined && (
        <SMLSwitch
          checked={isOn}
          onChange={toggle}
          disabled={disabled}
          ariaLabel={intl.formatMessage(m.serviceSwitch, { name })}
          testId={`catalog-service-${service}`}
        />
      )}
    </div>
  );
};

export const CatalogLinksServiceRow = TypedMemo(CatalogLinksServiceRowComponent);

/** Apple Music says "Pre-add" (as the public page). */
export function presaveChipOf(service: string): 'preAdd' | 'presave' {
  return service === MusicAPIIntegrationID.AppleMusic ? 'preAdd' : 'presave';
}
